import { Button, Checkbox, Collapse, ConfigProvider, Descriptions, Form, List, Modal, Steps, Table, Tabs, Tooltip } from "antd";
import { DownloadOutlined, InfoCircleFilled } from '@ant-design/icons';
import React, { MouseEvent } from "react";
import { damageCaseStatusOptions, enmDamageCaseStatus, enmEvaluationMethods, enmEvaluationStatus, evaluationMethodsOptions, evaluationStatusOptions, IDamageCase } from "../../baseInfo/damageCase/interfaces";
import { BaseComponent, IBaseProps, IBaseStates } from "../../components/infrastructure/baseComponent";
import { ICallOptions, IEntity, IResultSet, IUserInfo } from "../../components/infrastructure/models";
import "../index.css";
import dayjs from 'dayjs';
import { Core } from "../../components/infrastructure/core";
import { IDamageCaseEvaluator, enmContactStatus } from "./interfaces";
import * as EvaluationComponents from "./components";
import ReactDOM from "react-dom";
import AppContext from "../../components/infrastructure/appContext";
import CustomSpinner from "../../components/infrastructure/customSpinner";
import { IAttachment } from "../../baseInfo/attachment/interfaces";
import { PriceInput } from "../../components/priceInput";
import { Redirect } from "react-router-dom";
import { jalaliLocale } from "../../home/jalaliLocale";

const { Step } = Steps;
const { Panel } = Collapse;
const { TabPane } = Tabs;

type LayoutType = Parameters<typeof Form>[0]['layout'];

export interface IEvaluationComponentProps extends IBaseProps {
    assignEvaluatorAccess?: boolean;
    damageCase: IDamageCase;
    nextStatus: (damageCase: IDamageCase, forceAssignEvaluator?: boolean) => void;
}

export interface IDamageCaseInquiryResult {
    PolicyholderNationalCode: string;
    PolicyholderFullName: string;
    IMEIorSerial: string;
    Code: number;
    DamageDate: any;
    PaymentDate?: any;
    PaidAmount?: number;
    Status: enmDamageCaseStatus;
}

export interface IEvaluationComponentStates<TStep extends IEntity> extends IBaseStates {
    title?: string;
    formLayout: LayoutType;
    currentEntity: TStep;
    damageCaseEvaluator: IDamageCaseEvaluator;
    evaluationSteps: { DamageCaseEvaluatorId: number; Substatus: enmEvaluationStatus; CreateDateTime: any; HasNextStep?: boolean; }[];
    policyCert?: IAttachment;
    allDamageCases: IDamageCaseInquiryResult[];
    damageCaseDocumentTypeAttachments: any[];
    policyUpdated?: boolean;
}

export interface IEvaluationInitialData {
    EvaluationSteps: { DamageCaseEvaluatorId: number; Substatus: enmEvaluationStatus; CreateDateTime: any; HasNextStep?: boolean; }[];
    PolicyCert?: IAttachment;
    AllDamageCases: IDamageCaseInquiryResult[];
    DamageCaseDocumentTypeAttachments: any[];
}

export interface IEvaluationComponentFormProps<TEntity> {
    loading: boolean;
    disabled?: boolean;
    currentEntity: TEntity;
    damageCase: IDamageCase;
    onClose?: (sender: any) => void;
    onSubmit?: (values: any) => void;
    damageCaseDocumentTypeAttachments: IAttachment[];
}

const EvaluationComponentFormModal = (props: {
    damageCase: IDamageCase,
    entity: { Substatus: enmEvaluationStatus; },
    damageCaseDocumentTypeAttachments: IAttachment[],
    evaluationSteps: { DamageCaseEvaluatorId: number; Substatus: enmEvaluationStatus; CreateDateTime: any; HasNextStep?: boolean; }[],
    index: number,
    evaluationMethodChanged?: boolean
}) => {

    const handleCancel = () => {
        const target = document.getElementById('modal-container');
        ReactDOM.unmountComponentAtNode(target as any);
    };

    let formName = enmEvaluationStatus[Number(props.entity.Substatus)] + "Form";
    let formProps: IEvaluationComponentFormProps<any> = {
        currentEntity: props.entity,
        loading: false,
        disabled: true,
        damageCase: props.damageCase,
        damageCaseDocumentTypeAttachments: props.damageCaseDocumentTypeAttachments
    };

    const Component = (EvaluationComponents as any)[formName];
    if (!Component)
        return (<></>);

    if (formName === "ContactSettingForm") {
        (formProps as any).evaluationMethodChanged = props.evaluationMethodChanged;
    }

    return (
        <ConfigProvider locale={jalaliLocale} direction="rtl">
            <Modal width={"100%"}
                visible={true}
                okButtonProps={{ hidden: true }}
                onCancel={handleCancel}
                closable={false}
                centered
            >
                <Component key={formName} {...formProps} />
            </Modal>
        </ConfigProvider>
    );
}

export class EvaluationComponent<TStep extends IEntity, TProps extends IEvaluationComponentProps, TStates extends IEvaluationComponentStates<TStep>> extends BaseComponent<TProps, TStates> {
    static contextType = AppContext;
    userInfo?: IUserInfo;
    defaultState: TStates = {} as any;
    defaultEntity?: TStep;
    statusName: string;
    onInitialDataFetching?: (initialParams: any) => any;
    onInitialDataFetched?: (result: IResultSet<any>) => any;
    extraHeaderButtons?: () => React.ReactNode;
    extraHeaderDescriptions?: () => React.ReactNode;
    constructor(props: TProps) {
        super(props)
        this.onInitializing();
        const status = evaluationStatusOptions.find(o => o.value === this.props.damageCase.EvaluationStatus);
        const title = status?.name;
        this.statusName = enmEvaluationStatus[Number(this.props.damageCase.EvaluationStatus)];
        const lastIndex = this.props.damageCase.DamageCaseEvaluators.length - 1;
        const damageCaseEvaluator = this.props.damageCase.DamageCaseEvaluators[lastIndex];
        const entity = Object.assign({}, {
            DamageCaseEvaluatorId: damageCaseEvaluator.Id,
            RowGuid: Core.newId()
        }, this.defaultEntity ?? {});

        this.state = Object.assign({}, {
            isAuthorized: true,
            loading: true,
            title,
            formLayout: 'horizontal',
            damageCaseEvaluator,
            evaluationSteps: [],
            allDamageCases: [],
            currentEntity: entity,
            windowSize: window.innerWidth <= 765 ? 1 : 2
        }, this.defaultState) as any;
    }
    onInitializing() {

    }
    componentDidMount() {
        super.componentDidMount();
        const { userInfo } = this.context;
        this.userInfo = userInfo;
        this.getInitialData();
    }
    async getInitialData() {
        let initialParams = {
            damageCaseEvaluatorId: this.state.damageCaseEvaluator.Id
        };
        if (this.onInitialDataFetching)
            initialParams = this.onInitialDataFetching(initialParams);
        let fetchOpt: ICallOptions = {
            url: "damagecaseevaluator/get" + this.statusName + "InitialData",
            data: initialParams,
            onSuccess: (result: IResultSet<IEvaluationInitialData>) => {
                if (result.ValidationState.Successful && result.Data) {
                    result.Data.EvaluationSteps = result.Data.EvaluationSteps.sort((a, b) => a.CreateDateTime > b.CreateDateTime ? 1 : -1) ?? [];
                    this.onInitialDataFetched ? this.onInitialDataFetched(result) : this.setState({
                        loading: false,
                        evaluationSteps: result.Data.EvaluationSteps,
                        damageCaseDocumentTypeAttachments: result.Data.DamageCaseDocumentTypeAttachments ?? [],
                        policyCert: result.Data.PolicyCert,
                        allDamageCases: result.Data.AllDamageCases
                    });
                }
            }
        };
        return await this.callApi(fetchOpt);
    }
    renderBody(): React.ReactNode {
        return (<>{this.state.title}</>);
    }
    showHistory(damageCase: IDamageCase, entity: { Substatus: enmEvaluationStatus; }, index: number, evaluationMethodChanged?: boolean) {
        const attachments: IAttachment[] = [];
        if (entity.Substatus === enmEvaluationStatus.DocumentCheck) {
            const prevCheckCount = this.state.evaluationSteps.filter((v, vIndex) => v.Substatus === entity.Substatus && index > vIndex).length;
            const lst = this.state.damageCaseDocumentTypeAttachments.filter((_, k) => k <= prevCheckCount).flatMap(a => a);
            attachments.push(...lst);
        }
        const target = document.getElementById('modal-container');
        const component = (
            <EvaluationComponentFormModal
                index={index}
                damageCase={damageCase}
                entity={entity}
                evaluationMethodChanged={evaluationMethodChanged}
                key="EvaluationComponentFormModal"
                damageCaseDocumentTypeAttachments={attachments}
                evaluationSteps={this.state.evaluationSteps} />
        );
        ReactDOM.render(component, target);
    }
    policyUpdate = async (event: MouseEvent<HTMLElement>) => {
        event.stopPropagation();

        const { damageCase } = this.props;
        const policyEquipmentModel = damageCase.PolicyEquipmentModel;
        const policy = policyEquipmentModel.Policy;
        const policyholderNationalCode = policy.Policyholder?.NationalCode;
        if (!policyholderNationalCode) {

            return;
        }
        let fetchOpt: ICallOptions = {
            url: "damagecaseevaluator/UpdateCurrentPoliciesOfNationalCode",
            data: {
                nationalCode: policyholderNationalCode
            },
            onSuccess: (result: IResultSet<boolean>) => {
                if (!result.ValidationState.Successful)
                    return;

                if (!result.Data) {
                    Core.errorNotification("بروزرسانی اطلاعات بیمه نامه بدلیل ناقص بودن اطلاعات دریافتی از سرویس صدور بیمه نامه، امکانپذیز نیست");
                    return;
                }

                Core.successNotification("بروزرسانی اطلاعات بیمه نامه با موفقیت انجام شد");
                this.setState({ policyUpdated: true });
            }
        };
        return await this.callApi(fetchOpt);

    }
    virtualRender() {
        const { damageCase } = this.props;

        if (this.state.policyUpdated) {
            const url = encodeURIComponent('/damagecaseevaluator?id=' + damageCase.RowGuid);
            return <Redirect to={`/refresh?${url}`} />
        }

        const extraHeaderNodes = () => {
            const { allDamageCases, policyCert, loading } = this.state;

            const policyEquipmentModel = damageCase.PolicyEquipmentModel;
            const policy = policyEquipmentModel.Policy;
            const policyholderNationalCode = policy.Policyholder?.NationalCode;
            const policyholderFullName = policy.Policyholder?.FullName;
            const equipmentSerialNo = policyEquipmentModel.EquipmentSerialNo;
            const equipmentName = policyEquipmentModel.Equipment.Name;
            const brandName = policyEquipmentModel.BrandTitle;
            const equipmentModelName = policyEquipmentModel.EquipmentModelTitle;

            const policyInfoTitle = <>
                <span> اطلاعات بیمه نامه
                    {!loading
                        ? <Tooltip title="جهت بروزرسانی اطلاعات بیمه نامه کلیک نمایید">
                            <Button style={{ padding: 0, height: 'auto' }} type="link" onClick={this.policyUpdate}>
                                <span><InfoCircleFilled className="blink" style={{ marginTop: 2, marginRight: 2, marginLeft: 1, verticalAlign: 'text-top' }} />بروزرسانی</span>
                            </Button>
                        </Tooltip>
                        : <></>
                    }
                </span>
            </>;

            const closedDamageCaseCount = allDamageCases.filter(f => [enmDamageCaseStatus.Closed, enmDamageCaseStatus.Rejected, enmDamageCaseStatus.Financial].indexOf(f.Status) > -1).length;
            const currentDamageCaseCount = allDamageCases.filter(f => [enmDamageCaseStatus.Closed, enmDamageCaseStatus.Rejected, enmDamageCaseStatus.Financial].indexOf(f.Status) === -1).length;
            const damageCaseHistoryTitle = `سوابق پرونده های خسارت (تمام شده ${closedDamageCaseCount}) (جاری ${currentDamageCaseCount})`;

            return (
                <div>
                    <Descriptions key={"Descriptions"} className="header-page" column={{ xxl: 5, xl: 4, lg: 3, md: 2, sm: 1, xs: 1 }}>
                        <Descriptions.Item label="شماره پرونده خسارت">{damageCase.Code}</Descriptions.Item>
                        <Descriptions.Item label="شماره تیکت">{damageCase.TicketNo}</Descriptions.Item>
                        <Descriptions.Item label="دلیل خسارت">{damageCase.DamageReason}</Descriptions.Item>
                        <Descriptions.Item label="تاریخ خسارت">{dayjs(damageCase.DamageDate).calendar('jalali').locale('fa').format("YYYY/MM/DD")}</Descriptions.Item>
                        <Descriptions.Item label="شماره تماس بیمه گزار/مشتری">{(damageCase.EmergencyCallNumber.startsWith('0') ? '' : '0') + damageCase.EmergencyCallNumber}</Descriptions.Item>
                        <Descriptions.Item label="بیمه گزار/مشتری">{policyholderFullName + " " + policyholderNationalCode}</Descriptions.Item>
                        {
                            !!policyCert &&
                            <Descriptions.Item label={<DownloadOutlined />}>
                                <a href={policyCert.Path} download={policyCert.Title} title="دانلود">
                                    گواهی بیمه نامه
                                </a>
                            </Descriptions.Item>
                        }
                        <Descriptions.Item label="نوع تجهیز">{equipmentName}</Descriptions.Item>
                        {brandName && <Descriptions.Item label="برند">{brandName}</Descriptions.Item>}
                        {equipmentModelName && <Descriptions.Item label="مدل">{equipmentModelName}</Descriptions.Item>}
                        <Descriptions.Item label="سریال مدل تجهیز (IMEI)">{equipmentSerialNo}</Descriptions.Item>
                        {damageCase.DamageType
                            ? <Descriptions.Item label="نوع خسارت">{damageCase.DamageType?.Name}</Descriptions.Item>
                            : <></>
                        }
                        <Descriptions.Item label="تایید انتقال مالکیت توسط بیمه گزار/مشتری"><Checkbox disabled defaultChecked={damageCase.ConfirmPossessionTransition} /></Descriptions.Item>
                        {damageCase.Description
                            ? <Descriptions.Item label="توضیحات بیمه گزار/مشتری">{damageCase.Description}</Descriptions.Item>
                            : <></>
                        }
                        {this.extraHeaderDescriptions && this.extraHeaderDescriptions()}
                    </Descriptions>
                    <Collapse key="ExtraInfo" >
                        <Panel header={policyInfoTitle} key="1" style={{ padding: 0, margin: 0 }}  >
                            <Tabs defaultActiveKey="1">
                                <TabPane
                                    tab={
                                        <span style={{ marginRight: 4 }}>
                                            جزئیات
                                        </span>
                                    }
                                    key="1"
                                    style={{ margin: 10 }}

                                >
                                    <Descriptions key={"PolicyDetails"} column={{ xxl: 4, xl: 4, lg: 2, md: 2, sm: 1, xs: 1 }}>
                                        <Descriptions.Item label="سریال بیمه نامه">{policy.SerialNo}</Descriptions.Item>
                                        <Descriptions.Item label="طرح">{policy.PlanName}</Descriptions.Item>
                                        <Descriptions.Item label="تاریخ شروع">{dayjs(policy.StartDateTime).calendar('jalali').locale('fa').format("YYYY/MM/DD")}</Descriptions.Item>
                                        <Descriptions.Item label="تاریخ پایان">{dayjs(policy.EndDateTime).calendar('jalali').locale('fa').format("YYYY/MM/DD")}</Descriptions.Item>
                                        <Descriptions.Item label="سرمایه بیمه نامه"><PriceInput style={{ width: 'auto', marginTop: -4 }} value={policyEquipmentModel.InsuredValue} disabled /></Descriptions.Item>
                                    </Descriptions>
                                </TabPane>
                                <TabPane
                                    tab={
                                        <span style={{ marginRight: 4 }}>
                                            پوشش ها
                                        </span>
                                    }
                                    key="2"
                                    style={{ margin: 10 }}
                                >
                                    <List
                                        itemLayout="horizontal"
                                        dataSource={policy.PolicyCoverages.map(c => { return { title: c.CoverageTitle } })}
                                        renderItem={item => (
                                            <List.Item>
                                                <List.Item.Meta
                                                    title={
                                                        <Descriptions key={"PolicyCoverages"} column={{ xxl: 2, xl: 2, lg: 2, md: 2, sm: 1, xs: 1 }}>
                                                            <Descriptions.Item label="عنوان">{item.title}</Descriptions.Item>
                                                        </Descriptions>
                                                    }
                                                    style={{ height: 25 }}
                                                />
                                            </List.Item>
                                        )}
                                    />
                                </TabPane>
                                <TabPane
                                    tab={
                                        <span>
                                            فرانشیز ها
                                        </span>
                                    }
                                    key="3"
                                    style={{ margin: 10 }}
                                >
                                    <List
                                        itemLayout="horizontal"
                                        dataSource={policy.PolicyFranchises.map(f => { return { title: f.FranchiseTitle, percent: f.FranchisePercent } })}
                                        renderItem={item => (
                                            <List.Item>
                                                <List.Item.Meta
                                                    title={
                                                        <Descriptions key={"PolicyFranchises"} column={{ xxl: 2, xl: 2, lg: 2, md: 2, sm: 1, xs: 1 }}>
                                                            <Descriptions.Item label="عنوان">{item.title}</Descriptions.Item>
                                                            <Descriptions.Item label="درصد">{item.percent}</Descriptions.Item>
                                                        </Descriptions>
                                                    }
                                                />
                                            </List.Item>
                                        )}
                                    />
                                </TabPane>
                            </Tabs>
                        </Panel>
                        {allDamageCases.length > 0 &&
                            <Panel header={<span> {damageCaseHistoryTitle} </span>} key="2" style={{ padding: 0, margin: 0 }}  >
                                <Table
                                    rowKey={row => row.Code}
                                    scroll={{ x: 'max-content' }}
                                    dataSource={allDamageCases}
                                    rowClassName={(record, _) => {
                                        let rowClassName = [enmDamageCaseStatus.Closed, enmDamageCaseStatus.Rejected, enmDamageCaseStatus.Financial].indexOf(record.Status) > -1 ? "closed-record" : "inprocess-record";
                                        if (record.Code === damageCase.Code)
                                            rowClassName = "current-record";
                                        return rowClassName;
                                    }}
                                    columns={[
                                        {
                                            dataIndex: "Info",
                                            render: (_, record) => {
                                                if ([enmDamageCaseStatus.Closed, enmDamageCaseStatus.Rejected, enmDamageCaseStatus.Financial].indexOf(record.Status) > -1)
                                                    return "تمام شده";
                                                else if (record.Code === damageCase.Code)
                                                    return "پرونده فعلی";
                                                else
                                                    return "جاری";
                                            }
                                        },
                                        {
                                            dataIndex: "PolicySerialNo",
                                            title: "سریال بیمه نامه"
                                        },
                                        {
                                            dataIndex: "Code",
                                            title: "شماره پرونده"
                                        },
                                        {
                                            dataIndex: "IMEIorSerial",
                                            title: "سریال یا IMEI"
                                        },
                                        {
                                            dataIndex: "Policyholder",
                                            title: "بیمه گزار/مشتری",
                                            render: (_, record) => {
                                                return `${record.PolicyholderFullName} ${record.PolicyholderNationalCode}`;
                                            }
                                        },
                                        {
                                            dataIndex: "DamageDate",
                                            title: "تاریخ خسارت",
                                            render: (_, record) => {
                                                return dayjs(record.DamageDate).calendar('jalali').locale('fa').format("YYYY/MM/DD")
                                            }
                                        },
                                        {
                                            dataIndex: "Status",
                                            title: "وضعیت پرونده",
                                            render: (_, record) => {
                                                const current = damageCaseStatusOptions.find(o => o.value === record.Status);
                                                return current?.title;
                                            }
                                        },
                                        {
                                            dataIndex: "PaymentDate",
                                            title: "تاریخ پرداخت",
                                            render: (_, record) => {
                                                if (record.PaymentDate)
                                                    return dayjs(record.PaymentDate).calendar('jalali').locale('fa').format("YYYY/MM/DD")
                                            }
                                        },
                                        {
                                            dataIndex: "PaidAmount",
                                            title: "مبلغ پرداخت شده",
                                            render: (_, record) => {
                                                if (record.PaidAmount)
                                                    return `${record.PaidAmount}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                                            }
                                        }
                                    ]} />
                            </Panel>
                        }
                    </Collapse>
                </div>
            );
        };

        const header = this.getPageHeader({
            title: this.state.title!,
            onBack: () => { this.props.onClose && this.props.onClose(this) },
            extraHeaderButtons: this.extraHeaderButtons && this.extraHeaderButtons(),
            extraHeaderNodes: extraHeaderNodes()
        });

        const len = this.state.evaluationSteps.length + 1;
        const steps = (
            <Steps current={len} direction="vertical" size="small" style={{ margin: 10 }}>
                <Step key="0" title="تخصیص ارزیاب" description={<span style={{ direction: "ltr", float: "right" }}>{dayjs(this.state.damageCaseEvaluator.CreateDateTime).calendar('jalali').locale('fa').format("YYYY/MM/DD HH:mm")}</span>} />
                {this.state.evaluationSteps.map((s, i) => {
                    let title = evaluationStatusOptions.find(o => o.value === s.Substatus)?.name;

                    const evaluationMethod = damageCase.EvaluationMethod;
                    const evaluationMethodChanged = !!this.state.evaluationSteps.find((v: any, vIndex) => v.ContactStatus === enmContactStatus.EvaluationMethodToPolicyholderInPerson && i <= vIndex);
                    if (s.Substatus === enmEvaluationStatus.ContactSetting)
                        title = evaluationMethodsOptions.find(m => (evaluationMethodChanged && m.value === enmEvaluationMethods.VideoCall) || m.value === evaluationMethod)?.contactSettingCommandTitle;
                    if (s.Substatus === enmEvaluationStatus.MakingContact)
                        title = evaluationMethodsOptions.find(m => (evaluationMethodChanged && m.value === enmEvaluationMethods.VideoCall) || m.value === evaluationMethod)?.makingContactCommandTitle;

                    let repeatedStep = !!this.state.evaluationSteps.find((v, vIndex) => v.Substatus === s.Substatus && i > vIndex);
                    if (repeatedStep)
                        title += ' (مجدد) ';
                    let dateTime = dayjs(s.CreateDateTime).calendar('jalali').locale('fa').format("YYYY/MM/DD HH:mm");
                    return (
                        <Step key={i + 1} title={title} description={<span style={{ direction: "ltr", float: "right" }}>{dateTime}</span>} onClick={() => this.showHistory(this.props.damageCase, s, i, evaluationMethodChanged)} style={{ cursor: "pointer" }} />
                    );
                })}
                <Step key={len} title={this.state.title + (this.state.evaluationSteps.filter(s => s.Substatus === damageCase.EvaluationStatus).length ? ' (مجدد) ' : '')} />
            </Steps>
        );
        const paddingRight = this.state.windowSize === 1 ? 0 : 10;
        const marginBottom = this.state.windowSize === 1 ? 10 : 0;
        return (
            <>
                <div id="modal-container" />
                {header}
                {this.state.loading ? <CustomSpinner /> :
                    <div key="container" className="row" style={{ margin: 0 }}>
                        <div key="steps" className="col-md-3 box" style={{ background: '#fff', marginBottom: marginBottom }} >
                            {steps}
                        </div>

                        <div key="form" className="col-md-9" style={{ padding: 0, paddingRight: paddingRight }}>
                            <div className="box" style={{ padding: '5px 45px', marginTop: '0px 0px', background: '#fff' }}>
                                {this.renderBody()}
                            </div>
                        </div>
                    </div>
                }
            </>
        );
    }
}